// Services
import breederService from "@/app/services/main/breeder/breeder-service.js";

// Utils
import { vuelidateUtil } from "@/utils/vuelidate-util.js";

// Mixins
import { toastMessageMixins } from "@/app/mixins/utils/toast-message-mixins";
import { imageUploadMixins } from "@/app/mixins/utils/image-upload-mixins";
import { inputTextMixins } from "@/app/mixins/utils/input-text-mixins";

// Constants
import { STATUS_CODES, STATES } from "@/app/constants/constants";

// Component
const FileUpload = () =>
    import(
        /* webpackPrefetch: true, webpackChunkName: "file-upload" */ "@/app/views/main-view/_shared/_components/file-upload/file-upload.vue"
    );

export default {
    name: "BreederBioFormContainer",
    components: { FileUpload },
    mixins: [toastMessageMixins, imageUploadMixins, inputTextMixins],
    data() {
        return {
            // Breeder Details
            breederDetails: {
                breederSysId: "",
                breederId: "",
                state: "",
                emailAddress: "",
                mobileNumber: "",
                status: "DEACTIVATED",
                companyName: "",
                name: "",
                address: "",
            },

            originalBreederDetails: {},

            // Breeder Bio
            breederBio: {
                ourPlace: "",
                puppyReady: "",
                healthWell: "",
                termCon: "",
            },

            originalBreederBio: {},
            isBreederBioEmpty: false,

            isBreederAvailable: false,
            verified: false,
            status: "",
            isDetailsUpdated: false,

            // Image upload
            imageToCrop: "",
            imageSrc: "",
            fileBlob: null,

            // States
            stateOptions: [
                {
                    value: "NSW",
                    option: "New South Wales",
                },
                {
                    value: "VIC",
                    option: "Victoria",
                },
                {
                    value: "QLD",
                    option: "Queensland",
                },
                {
                    value: "WA",
                    option: "Western Australia",
                },
                {
                    value: "SA",
                    option: "South Australia",
                },
                {
                    value: "TAS",
                    option: "Tasmania",
                },
                {
                    value: "NT",
                    option: "Northern Territory",
                },
                {
                    value: "ACT",
                    option: "Australian Capital Territory",
                },
            ],

            // Loading
            isAPILoading: false,
            isSendingAPIRequest: false,

            // Mobile Number Format
            mobileNumberIsValid: true,
            breederMobileNumberIsValid: true,
        };
    },
    watch: {},
    async created() {
        await this.getBreederDetails();
    },

    computed: {
        hasImage() {
            return !!this.imageSrc;
        },

        selectedState() {
            let state = "";

            if (this.breederDetails.state) {
                state = STATES[this.breederDetails.state];
            } else {
                state = "Select State";
            }

            return state;
        },

        validationRules() {
            let fields = {
                breederDetails: {
                    breederId: {},
                    state: {},
                    emailAddress: {},
                    mobileNumber: {},
                    companyName: {},
                },
            };

            fields.breederDetails = vuelidateUtil.setFieldsRequired(
                fields.breederDetails,
                true
            );
            fields.breederDetails = vuelidateUtil.setEmailValidation(
                fields.breederDetails,
                "emailAddress"
            );

            return fields;
        },

        isDisabled() {
            return this.$v.$invalid || this.isAPILoading;
        },

        verifiedStatus() {
            return this.verified ? "Verified Breeder" : "Unverified Breeder";
        },

        enableField() {
            return (
                this.status === "P" ||
                this.status === "DEACTIVATED" ||
                this.status === "FOR_REACTIVATION"
            );
        },
    },

    methods: {
        async getBreederDetails() {
            const breederId = this.$route.params.id;

            try {
                this.isAPILoading = true;
                const response = await breederService.getBreederDetailsById(
                    breederId
                );

                if (response.status === 200) {
                    const responseData = response.data;

                    // Set details from API
                    // this.$store.commit("setBreederDetails", {
                    //     breederSysId: responseData.id,
                    //     name: responseData.name,
                    // });

                    this.breederDetails.breederSysId = responseData.id;
                    this.breederDetails.breederId = responseData.code;
                    this.breederDetails.name = responseData.name;
                    this.breederDetails.state = responseData.state;
                    this.breederDetails.emailAddress = responseData.email;
                    this.breederDetails.mobileNumber =
                        responseData.mobileNumber.replace(
                            /(....)(......)/g,
                            "$1-$2"
                        );
                    this.breederDetails.address = responseData.address;
                    this.breederDetails.companyName = responseData.companyName;
                    this.imageSrc = responseData.imageUrl;
                    this.verified = responseData.verified;
                    this.status = responseData.status;
                    this.breederDataId = responseData.id;

                    // breeder bio ----------------
                    const {
                        id: bioId,
                        ourPlace,
                        puppyReady,
                        healthWell,
                        termCon,
                    } = responseData.breederBio;

                    this.breederBio.ourPlace = ourPlace;
                    this.breederBio.puppyReady = puppyReady;
                    this.breederBio.healthWell = healthWell;
                    this.breederBio.termCon = termCon;

                    // the label of the submit form button will be based on the state of isBreederBioEmpty
                    if (!bioId) {
                        this.isBreederBioEmpty = true;
                    } else {
                        this.isBreederBioEmpty = false;
                    }

                    this.originalBreederDetails = { ...this.breederDetails };
                    this.originalBreederBio = { ...this.breederBio };

                    this.isBreederAvailable = true;
                    this.isAPILoading = false;
                } else {
                    this.isBreederAvailable = false;
                    this.isAPILoading = false;
                }
            } catch (err) {
                // Catch any error
                this.isAPILoading = false;
            }
        },

        onSelectedState(state) {
            this.breederDetails.state = state.value;
        },

        async onUploadImage(event) {
            let valid = this.validateImage(event.target.files[0]);

            // compress image if more than 2mb
            const file = await this.compressImage(event.target.files[0]);

            // do nothing when user cancels Upload
            if (!valid) {
                // reset uploader
                this.$refs.uploader.value = "";
                return;
            }

            this.fileBlob = [file];

            // show preview of pet image
            let reader = new FileReader();
            reader.onload = () => {
                this.imageToCrop = reader.result;
                this.$refs.modalFileUpload.show();
            };

            reader.readAsDataURL(file);
        },

        setImageCrop({ image, blob }) {
            // this.isImageChanged = true;
            this.imageSrc = image;
            this.fileBlob = blob;
        },

        clearFileInput(event) {
            event.target.value = "";
        },

        mobileNumberFormat(value, isBreeder) {
            this.mobileNumberIsValid = true;

            let regexp = /^\d+$/;
            let updateData = regexp.test(value.charAt(value.length - 1));
            const val = value.replace("-", "");

            if (updateData && val.length <= 10) {
                if (isBreeder) {
                    this.breederDetails.mobileNumber = val.replace(
                        /(....)(......)/g,
                        "$1-$2"
                    );
                } else {
                    this.fields.mobileNumber = val.replace(
                        /(....)(......)/g,
                        "$1-$2"
                    );
                }
            } else {
                if (isBreeder) {
                    this.breederDetails.mobileNumber = !updateData
                        ? val.slice(0, -1)
                        : this.breederDetails.mobileNumber;
                } else {
                    this.fields.mobileNumber = !updateData
                        ? val.slice(0, -1)
                        : this.fields.mobileNumber;
                }
            }
        },

        checkFormat() {
            let mobileNumber = this.breederDetails.mobileNumber;
            let validMobile = this.breederMobileNumberIsValid;

            this.$v.breederDetails.mobileNumber.$touch();

            let regexp = /^\d{4}-\d{6}$/;

            if (mobileNumber !== null && mobileNumber !== "") {
                validMobile = regexp.test(mobileNumber);
            } else {
                validMobile = true;
            }

            this.mobileNumberIsValid = validMobile;
        },

        async submitBreederDetails() {
            try {
                this.isSendingAPIRequest = true;

                const responses = await Promise.all([
                    this.updateBreederDetails(),
                    this.updateBreederBio(),
                ]);

                const areRequestsSuccess = responses.every(
                    (res) => res === STATUS_CODES.SUCCESS_NO_CONTENT
                );
                if (areRequestsSuccess) {
                    this.showCustomSuccessToast(
                        null,
                        "Breeder details has been successfully updated."
                    );
                }

                this.getBreederDetails();
            } catch (err) {
                console.log(err);
            } finally {
                this.isSendingAPIRequest = false;
            }
        },

        async updateBreederDetails(requestType = "UPDATE") {
            try {
                const formattedAddress =
                    this.breederDetails.address.displayAddress ||
                    this.breederDetails.address;

                const isDetailsUpdated =
                    JSON.stringify(this.breederDetails) !==
                    JSON.stringify(this.originalBreederDetails);

                const details = {
                    requestDetails: {
                        breeder: {
                            id: this.breederDataId,
                            code: this.breederDetails.breederId,
                            state: this.breederDetails.state,
                            name: this.breederDetails.name,
                            email: this.breederDetails.emailAddress,
                            mobile: this.breederDetails.mobileNumber.replace(
                                "-",
                                ""
                            ),
                            address: formattedAddress,
                            companyName: this.breederDetails.companyName,
                        },
                        requestType,
                        isDetailsUpdated: isDetailsUpdated,
                    },
                    breederImg: this.fileBlob
                        ? this.fileBlob[0]
                        : this.isBreederAvailable
                        ? this.imageSrc
                        : "",
                };

                const resDetails = await breederService.updateBreederDetails(
                    details
                );

                if (resDetails.status !== STATUS_CODES.SUCCESS_NO_CONTENT) {
                    this.showAPIErrorToast(
                        "Unable to update breeder details.",
                        resDetails
                    );
                }

                return resDetails.status;
            } catch (err) {
                console.log(err);
            }
        },

        async updateBreederBio() {
            try {
                const breederBioDetails = {
                    breederSysId: this.breederDetails.breederSysId,
                    ...this.breederBio,
                };

                // action includes inserting new data or updating the existing ones
                const breederBioAction = this.isBreederBioEmpty
                    ? breederService.addBreederBio
                    : breederService.updateBreederBio;

                const resBio = await breederBioAction(breederBioDetails);

                if (resBio.status !== STATUS_CODES.SUCCESS_NO_CONTENT) {
                    this.showAPIErrorToast(
                        "Unable to update breeder bio.",
                        resBio
                    );
                } else {
                    this.isBreederBioEmpty = false;
                }

                return resBio.status;
            } catch (err) {
                console.error(err);
            }
        },
    },

    validations() {
        return this.validationRules;
    },
};
